// Monatsbericht in der KUNDENSICHT: zeigt ausschließlich Berichte, die im
// Admin-Bereich ausdrücklich fürs Kundenkonto freigegeben wurden (Migration 039).
// Ersetzt den manuellen Mail-Versand — der Kunde liest den Bericht in seinem Login.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, FileText, Copy, Download, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { listMyReports, getMyReport, openReportPdf, saveMyReportEmail } from '@/lib/analytics-api';
import { useAuthStore } from '@/hooks/useAuth';
import BerichteUebersicht from '@/components/BerichteUebersicht';
import PdfVorschau from '@/components/PdfVorschau';

/** „2026-06-01" → „Juni 2026" */
function monatLabel(month: string): string {
  const d = new Date(`${month.slice(0, 7)}-01T00:00:00Z`);
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export default function MonatsberichtPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [month, setMonth] = useState<string | null>(null);
  const [pdfLaeuft, setPdfLaeuft] = useState(false);

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['my-reports'],
    queryFn: listMyReports,
  });
  const reports = listData?.reports || [];

  // Berichts-Empfänger: Der Kunde pflegt selbst, wer die Mail zum neuen
  // Bericht bekommt — der Betrieb weiß das besser als die Agentur.
  const [empfaenger, setEmpfaenger] = useState<string | null>(null);
  useEffect(() => {
    if (empfaenger === null && listData !== undefined) setEmpfaenger(listData.report_email || '');
  }, [listData, empfaenger]);
  const empfaengerMut = useMutation({
    mutationFn: (wert: string) => saveMyReportEmail(wert),
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: ['my-reports'] });
      setEmpfaenger(d.report_email || '');
      toast.success(d.report_email ? 'Empfänger gespeichert' : 'Keine E-Mail-Benachrichtigung mehr');
    },
    onError: (e: any) => toast.error(e?.message || 'Speichern fehlgeschlagen'),
  });
  const empfaengerGeaendert = empfaenger !== null && empfaenger.trim() !== (listData?.report_email || '');

  // Neuesten Bericht automatisch öffnen — ein Klick weniger.
  useEffect(() => {
    if (!month && reports.length) setMonth(reports[0].month.slice(0, 7));
  }, [reports, month]);

  const { data: reportData, isFetching } = useQuery({
    queryKey: ['my-report', month],
    queryFn: () => getMyReport(month!),
    enabled: !!month,
  });
  const report = reportData?.report || null;

  const copy = async () => {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(report.content_md);
      toast.success('Bericht kopiert');
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  // Admin: Sammelansicht aller Kunden. Die Kundensicht darunter waere fuer Nico
  // nutzlos — sie zeigt nur Berichte seines EIGENEN Kundendatensatzes.
  if (isAdmin) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Monatsberichte</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Alle Berichte aller Kunden — durchsehen, als PDF prüfen und freigeben. Der Monatslauf am 1.
            legt hier die Entwürfe ab. Berichte gibt es ab September 2026; der erste Entwurf erscheint
            am 1. Oktober.
          </p>
        </div>
        <BerichteUebersicht />
      </div>
    );
  }

  if (listLoading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Monatsbericht</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Was im vergangenen Monat auf Ihrer Website passiert ist – verständlich zusammengefasst.
        </p>
      </div>

      {!reports.length ? (
        <div className="bg-white rounded-lg border border-gray-200 px-5 py-10 text-center">
          <FileText className="w-8 h-8 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Sobald der erste Monatsbericht fertig ist, finden Sie ihn hier.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
            {/* Monatsauswahl nur zeigen, wenn es mehr als einen Bericht gibt */}
            {reports.length > 1 ? (
              <div className="inline-flex flex-wrap gap-1">
                {reports.map((r) => {
                  const m = r.month.slice(0, 7);
                  return (
                    <button
                      key={r.id}
                      onClick={() => setMonth(m)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                        m === month ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50 border border-gray-200'
                      }`}
                    >
                      {monatLabel(r.month)}
                    </button>
                  );
                })}
              </div>
            ) : (
              <span className="text-sm font-medium text-gray-900">{month ? monatLabel(month) : ''}</span>
            )}
            {report && (
              <button
                onClick={async () => {
                  if (!month) return;
                  setPdfLaeuft(true);
                  try {
                    // Seit Migration 045 gibt es genau einen Bericht pro Monat;
                    // der Endpunkt nimmt automatisch den eigenen Kunden.
                    await openReportPdf(month);
                  } catch (e: any) {
                    toast.error(e?.message || 'PDF konnte nicht erzeugt werden');
                  } finally {
                    setPdfLaeuft(false);
                  }
                }}
                disabled={pdfLaeuft}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium disabled:opacity-60"
              >
                {pdfLaeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {pdfLaeuft ? 'Erstelle PDF…' : 'Als PDF herunterladen'}
              </button>
            )}
            {report && (
              <button
                onClick={copy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-medium"
              >
                <Copy className="w-3.5 h-3.5" /> Kopieren
              </button>
            )}
          </div>

          {isFetching && !report ? (
            <div className="px-4 py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
          ) : report ? (
            <div className="flex flex-col items-center gap-2 px-5 py-6">
              {/* Der Bericht IST das PDF. Der Markdown-Text stand hier nur
                  daneben und zeigte dasselbe in schlechterer Form. */}
              {month && (
                <>
                  <PdfVorschau month={month} titel={`Monatsbericht ${monatLabel(month)}`} breite={420} />
                  <p className="text-[11px] text-gray-400">Zum Vergrößern auf das Blatt klicken</p>
                </>
              )}
            </div>
          ) : (
            <div className="px-4 py-10 text-center text-sm text-gray-400">Bericht konnte nicht geladen werden.</div>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-gray-400" />
          <p className="text-sm font-medium text-gray-900">Benachrichtigung bei neuem Bericht</p>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Sobald ein neuer Monatsbericht bereitliegt, geht eine kurze E-Mail an diese Adresse(n).
          Mehrere mit Komma trennen — leer lassen, wenn Sie keine E-Mail möchten.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={empfaenger ?? ''}
            onChange={(e) => setEmpfaenger(e.target.value)}
            placeholder="inhaber@ihre-firma.de, buero@ihre-firma.de"
            className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            disabled={empfaengerMut.isPending}
          />
          <button
            onClick={() => empfaengerMut.mutate((empfaenger ?? '').trim())}
            disabled={!empfaengerGeaendert || empfaengerMut.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-40"
          >
            {empfaengerMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
